import React from 'react';
import { PhoneIcon } from 'lucide-react';
import { ScreenHeader } from '../components/ScreenHeader';
import { MetaLabel } from '../components/MetaLabel';
import { useShoot } from '../contexts/ShootContext';

/**
 * Visual pass 9 Sep 2026, at Tom's request ("make sure it looks visually
 * pleasing across the board... we need to be a premium service"). Two
 * things stood out against Today (the screen he's happy with):
 *
 * 1. Each contact used to sit in its own fully-bordered box with a gap
 *    between them — a "card grid" language nothing else in the app uses.
 *    Today, Schedule, Shots and Kit all use one continuous divided list
 *    (a bottom border between rows, no boxes). Contacts now matches that.
 * 2. A contact with no phone number showed a bordered "No #" box — reads
 *    like a broken/placeholder state, not a design choice. This app's
 *    running convention (see Today's Important Instructions/Files, the
 *    Shots tab, empty shot sections) is "leave it out, don't grey it
 *    out": if there's nothing to show, don't show a stand-in for it. A
 *    contact with no number now just has no call button — same row,
 *    quietly missing one element, instead of an apologetic box.
 *
 * Member name size/weight now also matches Shots.tsx's item title (18px
 * Archivo semibold) rather than its own one-off 21px, so "row title"
 * reads the same way in every list in the app.
 */
export function Crew() {
  const {
    crewGroups,
    production
  } = useShoot();
  return <div className="pb-10" data-id="element-55" data-mp="32q99oc1zkfoq">
      {/* Eyebrow relabelled "Crew" -> "Contacts" 9 Sep 2026, matching the
          bottom nav tab — this screen lists Portal's own team alongside
          client contacts and featured guests, not just crew. */}
      <ScreenHeader eyebrow="Contacts" title="On Call" meta={[production.date, production.location].filter(Boolean).join(' · ')} data-id="element-56" data-mp="3boupw0jzjimc" />

      {crewGroups.map(group => <section key={group.id} aria-label={group.title} className="mt-8 px-5" data-id="element-57" data-mp="2k019o2tl25cr">
          <div className="flex items-baseline justify-between border-b border-line pb-3" data-id="element-58" data-mp="myvdlmw5c0yb">
            <MetaLabel as="h2" tone="dim" data-id="element-59" data-mp="rxorvhj9bvzj">
              {group.title}
            </MetaLabel>
            <span className="text-[11px] tabular-nums text-dim" data-id="element-60" data-mp="woey0wzic2va">{group.members.length}</span>
          </div>

          <ul data-id="element-61" data-mp="11n8d2tug01xk">
            {group.members.map(member => <li key={member.id} className="flex items-center gap-4 border-b border-line py-4" data-id="element-62" data-mp="11mqcjl0qz1ju">
                <div className="min-w-0 flex-1" data-id="element-63" data-mp="41kga7u2rhli">
                  <p className="font-display text-[18px] font-semibold leading-tight tracking-display text-fg" data-id="element-64" data-mp="8yazh416etb5">
                    {member.name}
                  </p>
                  <p className="mt-1.5 text-[10px] font-medium uppercase tracking-label text-muted" data-id="element-65" data-mp="e0a0465j7c4e">
                    {member.role || '—'}
                  </p>
                </div>
                {member.phone ? <a href={`tel:${member.phone.replace(/\s/g, '')}`} aria-label={`Call ${member.name}`} className="flex h-11 w-11 shrink-0 items-center justify-center border border-lineHi text-fg transition-colors duration-150 ease-out hover:bg-fg hover:text-ink focus:outline-none focus-visible:ring-1 focus-visible:ring-fg" data-id="element-66" data-mp="1ubxaahsdnn41">
                    <PhoneIcon className="h-[18px] w-[18px]" strokeWidth={1.5} data-id="element-67" data-mp="305tq7ycjnmon" />
                  </a> : null}
              </li>)}
          </ul>
        </section>)}

      {crewGroups.length === 0 && <p className="px-5 pt-8 text-[13px] text-dim" data-id="element-68" data-mp="2r32bnpodgass">No crew assigned to this project yet.</p>}
    </div>;
}